import { type ReactNode } from 'react'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { Check, CheckCheck, FileText, Loader2, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAttachmentUrl } from '@/core/hooks/useChat'
import type { Mensaje } from '@/core/types/chat'

interface ChatMessageProps {
  mensaje: Mensaje
  isMine: boolean
  showSender?: boolean
  senderName?: string
  showTime?: boolean
  readBy?: string[]
  totalMembers?: number
  isDirect?: boolean
  highlightQuery?: string
}

function highlightText(text: string, query?: string): ReactNode {
  if (!query || query.length < 2) return text
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  const parts = text.split(regex)
  if (parts.length === 1) return text
  return parts.map((part, i) =>
    regex.test(part) ? <mark key={i} className="bg-yellow-300/60 dark:bg-yellow-700/60 rounded-sm px-0.5">{part}</mark> : part
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
}

/**
 * Renderiza un attachment resolviendo el signed URL on-demand.
 * El signed URL vive 1h y se refresca automáticamente (staleTime 50min).
 */
function ChatAttachment({
  path,
  type,
  name,
  size,
  isMine,
}: {
  path: string
  type: string | null
  name: string | null
  size: number | null
  isMine: boolean
}) {
  // Optimistic upload: el path es una blob URL local (blob:...) creada con
  // URL.createObjectURL() durante el upload. Se renderiza directo sin pedir
  // signed URL al backend — el mensaje real llegará via invalidate después.
  const isBlob = path.startsWith('blob:')
  const { data: fetchedUrl, isLoading } = useAttachmentUrl(isBlob ? null : path)
  const url = isBlob ? path : fetchedUrl
  const isImage = type?.startsWith('image/')

  if (isLoading && !isBlob) {
    return (
      <div className="mb-1 flex items-center gap-2 text-xs opacity-70">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Cargando archivo...</span>
      </div>
    )
  }

  // Con una renovación fallida sigue mostrando la URL anterior mientras el hook reintenta.
  if (!url) {
    return (
      <div className="mb-1 flex items-center gap-2 text-xs text-destructive">
        <AlertCircle className="h-3 w-3" />
        <span>No se pudo cargar el archivo</span>
      </div>
    )
  }

  return (
    <div className="mb-1">
      {isImage ? (
        <img
          src={url}
          alt={name ?? 'Imagen'}
          className="max-w-[200px] max-h-[200px] rounded-lg cursor-pointer object-cover"
          onClick={() => window.open(url, '_blank')}
          loading="lazy"
        />
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs',
            isMine ? 'bg-primary-foreground/10 hover:bg-primary-foreground/20' : 'bg-background/50 hover:bg-background/80'
          )}
        >
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate">{name}</span>
          {size != null && (
            <span className="text-[10px] opacity-70 shrink-0">
              {formatFileSize(size)}
            </span>
          )}
        </a>
      )}
    </div>
  )
}

export function ChatMessage({
  mensaje,
  isMine,
  showSender = false,
  senderName,
  showTime = true,
  readBy,
  totalMembers,
  isDirect,
  highlightQuery,
}: ChatMessageProps) {
  const isOptimistic = mensaje.id.startsWith('temp-')
  const isRead = readBy && readBy.length > 0
  // Prefer attachment_path (new). Fallback a attachment_url legacy solo por si hay data
  // antigua no migrada — el backfill debería cubrir todos los casos.
  const attachmentPath = mensaje.attachment_path ?? null
  const hasAttachment = !!attachmentPath

  return (
    <div className={cn('flex flex-col', isMine ? 'items-end' : 'items-start')}>
      {/* Sender name for groups/channels */}
      {showSender && !isMine && senderName && (
        <span className="text-[11px] font-medium text-muted-foreground ml-2 mb-0.5">
          {senderName}
        </span>
      )}
      <div
        className={cn(
          'max-w-[75%] rounded-2xl px-3.5 py-2 text-sm',
          isMine
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-muted rounded-bl-md',
          isOptimistic && 'opacity-70'
        )}
      >
        {/* Attachment */}
        {hasAttachment && attachmentPath && (
          <ChatAttachment
            path={attachmentPath}
            type={mensaje.attachment_type}
            name={mensaje.attachment_name}
            size={mensaje.attachment_size}
            isMine={isMine}
          />
        )}

        {/* Text content (hide if it's just the filename for attachments) */}
        {(!hasAttachment || mensaje.content !== mensaje.attachment_name) && (
          <p className="whitespace-pre-wrap break-words">
            {highlightQuery ? highlightText(mensaje.content, highlightQuery) : mensaje.content}
          </p>
        )}

        {showTime && (
          <div
            className={cn(
              'flex items-center justify-end gap-1 mt-1',
              isMine ? 'text-primary-foreground/70' : 'text-muted-foreground'
            )}
          >
            <span className="text-[10px]">
              {isOptimistic ? 'Enviando...' : format(parseISO(mensaje.created_at), 'HH:mm', { locale: es })}
            </span>
            {/* Read receipt indicator (only for own messages) */}
            {isMine && !isOptimistic && (
              isRead ? (
                <CheckCheck className="h-3.5 w-3.5 text-blue-300" />
              ) : (
                <Check className="h-3.5 w-3.5 opacity-70" />
              )
            )}
            {/* Group read count */}
            {isMine && !isOptimistic && !isDirect && isRead && totalMembers && totalMembers > 2 && (
              <span
                className="text-[9px] opacity-70"
                title={`Leído por ${readBy!.length} de ${totalMembers - 1}`}
              >
                {readBy!.length}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
